import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import uniqBy from "../../../helper/uniqBy";
import useAccessToken from "../../../hooks/useAccessToken";
import {
	getOutputReview,
	postOutputReview,
} from "../../../shared/services/wb-output-review-api-services";

import { createLogger } from "../../../helper/logger";

/**
 * Custom hook to handle data fetching and management for output review
 * @param {string} caseID - The case ID to fetch data for
 * @returns {Object} - Data and functions for the component
 */
export const useOutputReviewData = (caseID, usePagination = false) => {
	const logger = useMemo(() => createLogger("useOutputReviewData"), []);
	const accessTokenProvider = useAccessToken();
	const [loading, setLoading] = useState(false);
	const [rowData, setRowData] = useState([]);
	const [originalRowData, setOriginalRowData] = useState([]);
	const [totalRows, setTotalRows] = useState(0);

	// Create a datasource for AG Grid's infinite scrolling
	const createDatasource = useCallback(() => {
		return {
			// Function that AG Grid calls when it needs more rows
			getRows: async (params) => {
				logger.debug("AG Grid requested data", {
					startRow: params.startRow,
					endRow: params.endRow,
				});

				try {
					setLoading(true);
					const token = await accessTokenProvider.getToken();

					let result;

					if (usePagination) {
						// Calculate page number and size based on requested range
						const pageSize = params.endRow - params.startRow;
						// AG Grid is 0-based, API is 1-based for page numbers
						const pageNumber = Math.floor(params.startRow / pageSize) + 1;

						logger.debug("Fetching page from API with pagination", {
							pageNumber,
							pageSize,
							startRow: params.startRow,
							endRow: params.endRow,
						});

						// Request data from API with pagination
						result = await getOutputReview(caseID, token, pageNumber, pageSize);
					} else {
						logger.debug("Fetching all data from API without pagination");

						// Request all data from API without pagination parameters
						result = await getOutputReview(caseID, token);
					}

					if (result.data?.data && Array.isArray(result.data.data)) {
						const rowsThisBlock = result.data.data;
						logger.debug("Received data from API", {
							itemCount: rowsThisBlock.length,
							pagination: result.data.pagination,
						});

						// For the very first load, set the original data to establish baseline
						if (params.startRow === 0 && originalRowData.length === 0) {
							setOriginalRowData(JSON.parse(JSON.stringify(rowsThisBlock)));
						}

						// Append to our local copy of all loaded data
						// This is for internal tracking; AG Grid maintains its own cache
						if (rowsThisBlock.length > 0) {
							setRowData((prevData) => {
								const combinedData = [...prevData, ...rowsThisBlock];
								// Use uniqBy to ensure no duplicates by sn
								return uniqBy(combinedData, "sn");
							});
						}

						// Calculate last row based on pagination usage
						let lastRow = null;
						if (usePagination) {
							// When using pagination, calculate last row based on pagination metadata or page size
							if (result.data.pagination?.totalItems) {
								lastRow = result.data.pagination.totalItems;
								setTotalRows(lastRow);
							} else if (
								rowsThisBlock.length <
								params.endRow - params.startRow
							) {
								// If we received fewer rows than requested, we're at the end
								lastRow = params.startRow + rowsThisBlock.length;
								setTotalRows(lastRow);
							}
						} else {
							// When not using pagination, we have all data in one go
							lastRow = rowsThisBlock.length;
							setTotalRows(lastRow);
						}

						// Success callback tells AG Grid the data is ready
						params.successCallback(rowsThisBlock, lastRow);
					} else {
						// No data or error
						logger.debug("No data returned from API or error", result);
						params.successCallback([], 0);

						if (result?.error?.message) {
							logger.error("API returned error", {
								error: result.error.message,
							});
							toast.error(result.error.message);
						}
					}
				} catch (error) {
					logger.error("Error fetching data", error);
					params.failCallback();
					toast.error("Failed to load data. Please try again.");
				} finally {
					setLoading(false);
				}
			},
			// Tell AG Grid if we know the count upfront
			rowCount: totalRows > 0 ? totalRows : undefined,
		};
	}, [
		accessTokenProvider.getToken,
		caseID,
		logger,
		originalRowData.length,
		totalRows,
		usePagination,
	]);

	// For full data loading (for features like export all)
	const fetchAllData = useCallback(
		async (caseID) => {
			try {
				logger.debug("Starting data fetch for caseID", {
					caseID,
					usePagination,
				});
				setLoading(true);
				const token = await accessTokenProvider.getToken();
				logger.debug("Got access token");

				let allData = [];
				let errorMessage = null;

				if (usePagination) {
					// Use pagination approach
					let currentPage = 1;
					const pageSize = process.env.REACT_APP_RESULT_PAGE_SIZE; // Small page size for incremental loading
					logger.debug("Using pagination with page size", { pageSize });
					let hasMoreData = true;

					// Keep fetching until we get an empty array
					while (hasMoreData) {
						logger.debug("Fetching page", { page: currentPage });
						const result = await getOutputReview(
							caseID,
							token,
							currentPage,
							pageSize,
						);
						logger.debug("Response received", {
							page: currentPage,
							hasData: !!result?.data?.data,
							itemCount: result?.data?.data?.length || 0,
						});

						// Updated to handle the new API response structure with data inside a data key
						if (
							result.data?.data &&
							Array.isArray(result.data.data) &&
							result.data.data.length > 0
						) {
							logger.debug("Found items in page", {
								count: result.data.data.length,
								page: currentPage,
							});
							allData = [...allData, ...result.data.data];
							logger.debug("Total data items so far", {
								total: allData.length,
							});
							setRowData(uniqBy(allData, "sn"));

							// Check if pagination metadata exists
							if (result.data.pagination) {
								logger.debug("Pagination metadata", result.data.pagination);
								setTotalRows(
									result.data.pagination.totalItems || allData.length,
								);
							} else {
								logger.debug("No pagination metadata, using data length");
								setTotalRows(allData.length);
							}

							currentPage++;
						} else {
							// No more data to fetch
							logger.debug("No more data to fetch or empty response");
							logger.debug("Response structure", result);
							hasMoreData = false;
							// Store error message if it exists
							if (result?.error?.message) {
								errorMessage = result.error.message;
								logger.error("Error message", { error: errorMessage });
							}
						}
					}
				} else {
					// Fetch all data without pagination
					logger.debug("Fetching all data without pagination");
					const result = await getOutputReview(caseID, token);
					logger.debug("Response received", {
						hasData: !!result?.data,
						itemCount: result?.data?.length || 0,
					});

					if (
						result.data &&
						Array.isArray(result.data) &&
						result.data.length > 0
					) {
						allData = result.data;
						logger.debug("Found items", { count: allData.length });
						setRowData(allData);
						setTotalRows(allData.length);
					} else if (result?.error?.message) {
						errorMessage = result.error.message;
						logger.error("Error message", { error: errorMessage });
					}
				}

				if (allData.length > 0) {
					logger.debug("Setting final data", { count: allData.length });
					setOriginalRowData(JSON.parse(JSON.stringify(allData)));
					setRowData(allData);
					setTotalRows(allData.length);
					logger.debug("Data setup complete", { totalRows: allData.length });
				} else if (errorMessage) {
					logger.error("Error occurred", { error: errorMessage });
					toast.error(errorMessage);
				} else {
					logger.debug("No data found or empty data set");
				}
				setLoading(false);
			} catch (error) {
				logger.error("Error fetching data", error);
				setLoading(false);
				toast.error("Failed to load data. Please try again.");
			}
		},
		[accessTokenProvider.getToken, logger, usePagination],
	); // Initialize data - load all data incrementally
	useEffect(() => {
		logger.debug("Initializing with caseID", { caseID });
		fetchAllData(caseID);
	}, [caseID, fetchAllData, logger]);
	// Update row data when grid data changes
	const onRowDataUpdated = useCallback(
		(event) => {
			logger.debug("Grid data update event triggered");
			if (event?.api) {
				// Here's what was triggering the infinite render loop
				// I think AG grid is triggering a change when there's no change (Either that or the autocomplete)
				// We're going to push this change, try to do a normal workflow, if the data goes go through flawlessly, remove this callback and this comment
				return;
			}
		},
		[logger],
	);
	// Post data to server
	const postData = useCallback(
		async (editedRowData, action) => {
			logger.debug("Starting postData", { action });
			logger.debug("Edited row data", { count: editedRowData?.length || 0 });
			setLoading(true);

			const body = [];
			if (editedRowData && editedRowData.length > 0) {
				for (const row of editedRowData) {
					body.push({
						id: row.id,
						form_number: row.formNumber,
						permanent_status: row.permanentStatus,
						confirm_results: row.confirmResult,
					});
				}
				logger.debug("Prepared request body", { items: body.length });
			} else {
				logger.debug("No row data to process");
			}
			try {
				const token = await accessTokenProvider.getToken();
				logger.debug("Got access token for post request");
				const result = await postOutputReview(
					caseID,
					JSON.stringify(body),
					action,
					token,
				);
				logger.debug("Post request result", { success: !!result.data });

				setLoading(false);
				return result;
			} catch (error) {
				logger.error("Error in post request", error);
				setLoading(false);
				return { error: error.message || "An error occurred" };
			}
		},
		[caseID, accessTokenProvider, logger],
	); // Log the current state values before returning
	logger.debug("Current state on hook execution", {
		loading,
		rowDataLength: rowData?.length || 0,
		originalRowDataLength: originalRowData?.length || 0,
		totalRows,
	});

	// Create and return the datasource instance only when pagination is enabled
	const datasource = useMemo(() => {
		return usePagination ? createDatasource() : null;
	}, [createDatasource, usePagination]);

	return {
		loading,
		rowData, // Still return for compatibility with existing code
		originalRowData,
		totalRows,
		datasource, // New datasource for infinite scrolling (null when usePagination is false)
		onRowDataUpdated,
		postData,
		fetchAllData, // Expose this for cases where we need all data at once
	};
};
